import React, { useContext, useEffect, useState } from 'react';
import { AuthContext, ProfileContext } from '../../App';
import { useTranslation } from 'react-i18next';
import { flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, renderColumnHeader, TableProps, useReactTable } from '../Grid';
import { formatDate } from '../../util/Format';
import { get } from '../../util/HttpRequest';
import Waiting from '../Waiting';
import GridSummary from '../GridSummary';
import GridPagination from '../GridPagination';
import { Button } from 'antd';
import { handleGetFileResponse } from '../../util/File';
import { FilePdfIcon, NavAlertIcon } from '../Icons';


class ClaimLetterData {
    letterRequestId: number = 0;
    createdDateUTC: string = "";
    letterDescription: string = "";
    isRead: boolean = false;
};

class ClaimLettersData {
    recordCount: number = 0;
    refreshDate: Date = new Date();
    searchResults: ClaimLetterData[] = [];
};

function ClaimLetters() {
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [gridData, setGridData] = useState(new ClaimLettersData());
    const { accessToken, extendSession, user } = useContext(AuthContext);
    const { userProfileLossDraftsClaim } = useContext(ProfileContext);
    const { t } = useTranslation(["ClaimLetters", "DataGrid"]);
    // Props used by renderColumnHeader() from Grid.tsx to control header UI:
    // Includes sorting labels and optional class names for styling
    const columnHeaderProps = [
        {
            id: 'createdDateUTC', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        },
        {
            id: 'letterDescription', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        }
    ];

    useEffect(() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProfileLossDraftsClaim.claimId]);

    function refreshData() {
        setLoading(true);
        getData();
    };

    function getData() {
        extendSession();

        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/ClaimLetters");
        url.searchParams.set("UserId", user.id);
        url.searchParams.set("ClaimId", userProfileLossDraftsClaim.claimId.toString());
        console.log("Fetch Claim Letters from " + url);

        get(url, accessToken)
            .then(response => {
                if (!response.ok) { throw response }
                return response.json() as Promise<ClaimLetterData[]>
            })
            .then(searchResults => {
                const data = {
                    recordCount: searchResults.length,
                    refreshDate: new Date(),
                    searchResults: searchResults
                };
                setGridData(data);
                setErrorMsg("");
                setLoading(false);
            })
            .catch(error => {
                setGridData(new ClaimLettersData());
                setErrorMsg(error.message);
                setLoading(false);
            })
    };

    function getLetterFromAPI(letterRequestId, isDownload) {
        extendSession();

        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/ClaimLetterFile");
        url.searchParams.set("UserId", user.id);
        url.searchParams.set("ClaimId", userProfileLossDraftsClaim.claimId.toString());
        url.searchParams.set("LetterRequestId", letterRequestId.toString());

        console.log("Fetch Claim Letter File from " + url);

        get(url, accessToken)
            .then((response) => {
                handleGetFileResponse(response, isDownload);
                markAsRead(letterRequestId);
            })
            .catch(error => {
                setErrorMsg(error.message);
            });
    };

    function handleViewLetter(event, letterRequestId) {
        event.preventDefault();
        getLetterFromAPI(letterRequestId, false);

    };

    function markAsRead(letterRequestId){
        setGridData((prevData) => {
            const updatedResults = prevData.searchResults.map((letter) =>
              letter.letterRequestId === letterRequestId ? { ...letter, isRead: true } : letter
            );
            return { ...prevData, searchResults: updatedResults };
          });
    };

    function Table<TData extends object>({ columns, data }: TableProps<TData>) {
        const pageSize = Number(import.meta.env.VITE_GRID_PAGE_SIZE);
        // Use the state and functions returned from useReactTable to build your UI
        const table = useReactTable({
            data,
            columns,
            getCoreRowModel: getCoreRowModel(),
            getPaginationRowModel: getPaginationRowModel(), //load client-side pagination code
            getSortedRowModel: getSortedRowModel(),
            initialState: {
                pagination: {
                    pageIndex: 0, // Start on first page
                    pageSize: pageSize,
                },
            }, 
        });

        const filters = <></>;

        const pagination = (
            <GridPagination
                canPreviousPage={table.getCanPreviousPage()}
                canNextPage={table.getCanNextPage()}
                pageIndex={table.getState().pagination.pageIndex}
                pageCount={table.getPageCount()}
                gotoPage={table.setPageIndex}
                previousPage={table.previousPage}
                nextPage={table.nextPage}
            />
        );

        const summary = (
            <GridSummary
                pageIndex={table.getState().pagination.pageIndex}
                pageCount={table.getPageCount()}
                pageSize={pageSize}
                recordCount={data.length}
                recordType={t('gridRecordType', { count: data.length })}
                refreshDate={gridData.refreshDate}
                pagination={pagination}
            />
        );

        return (
            <>
                {filters}
                <br />
                {summary}
                <table className="table" role="table">
                    <thead role="rowgroup">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id} role="row">
                                {headerGroup.headers.map((header) =>
                                    renderColumnHeader(header, columnHeaderProps.find(x => x.id === header.column.id))
                                )}
                            </tr>
                        ))}
                    </thead>
                    <tbody role="rowgroup">
                        {table.getRowModel().rows.map((row, i) => (
                            <React.Fragment key={row.id}>
                                <tr className={"row-" + (i % 2 === 0 ? "even" : "odd")} role="row" tabIndex={0}>
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} role="cell">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </>
        )
    }

    function renderDataGrid() {
        const columns = [{
                id: 'readStatus',
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="datagrid-data-center">
                        <span className="action-icon">
                            {
                                row.original.isRead === false && (
                                    <NavAlertIcon class="medium-icon color-red" title={t('filterUnread')} />
                                )
                            }
                        </span>
                    </div>
                )
            }, {
            id: 'actions',
            enableSorting: false,
            cell: ({ row }) => (
                <div className="datagrid-data-center">
                    {
                        <Button type="link" className="icon-button" title={t('actionView')}
                            onClick={(e) => handleViewLetter(e, row.original.letterRequestId)}
                        >
                            {
                                <FilePdfIcon title={t('actionView')} />
                            }
                        </Button>
                    }

                </div>
            )
        }, {
            header: () => (<>{t('columnDate')}</>),
            accessorKey: 'createdDateUTC',
            enableSorting: true,
            cell: (info) => (
                <div className="datagrid-data-left">
                    {
                        formatDate(info.getValue())
                    }
                </div>
            )
        }, {
            header: () => (<>{t('columnDescription')}</>),
            accessorKey:'letterDescription',
            enableSorting: true
        }];

        const table = <Table
            columns={columns}
            data={gridData.searchResults}
        />;

        return <div>{table}</div>;
    };
    
    const title = <h2>{t('title')}</h2>;
    const contents = (errorMsg > '') ? <p><em>{errorMsg}</em></p> : loading ? <Waiting size='large' /> : renderDataGrid();
    return <div>{title}<br />{contents}</div>
};

export default ClaimLetters;